import { useEffect, useState } from "react";

import {
  msUntilNextDay,
  refreshQuotePool,
  resolveDailyQuote,
  type DailyQuoteResult,
  type Quote,
} from "@/lib/daily-quote";

export interface UseDailyQuoteResult {
  /** Null until the dataset has been fetched at least once on this device. */
  quote: Quote | null;
  /** True only while a fetch is genuinely in flight. */
  isLoading: boolean;
}

interface State {
  result: DailyQuoteResult | null;
  pending: boolean;
}

/**
 * The home page's daily quote.
 *
 * `resolveDailyQuote` is synchronous and reads a per-day pin out of
 * localStorage, so once the pool has been cached the first render already has
 * the right text — no skeleton, no swap-in, which is why `isLoading` is false
 * for every returning visitor.
 *
 * There is no offline fallback, so on a device that has never loaded the page
 * there is genuinely nothing to show until the dataset arrives.
 *
 * `isLoading` is deliberately **not** `result === null`. Those are different
 * states: a failed first fetch also leaves `result` null, and conflating the
 * two would leave the caller rendering a placeholder forever on a network
 * that is never going to answer.
 *
 * A tab left open across midnight re-resolves on the local day boundary, so a
 * reader who parks the home page overnight still wakes up to a new quote.
 */
export function useDailyQuote(): UseDailyQuoteResult {
  const [state, setState] = useState<State>(() => {
    const result = resolveDailyQuote();
    return { result, pending: result === null };
  });

  useEffect(() => {
    let cancelled = false;

    // Always run this, on every mount. It no-ops internally when the pool is
    // healthy, and that no-op is what keeps the monthly refresh alive for
    // returning visitors — guarding it behind "do I have a quote?" would
    // strand the pool once the window ran dry.
    void refreshQuotePool().then(() => {
      if (cancelled) return;
      setState((prev) => ({
        // Only re-resolve when there was nothing to show. Inside a day this
        // is a no-op anyway, so an already-pinned quote cannot move.
        result: prev.result ?? resolveDailyQuote(),
        pending: false,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let midnightTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleNextDay = () => {
      // setTimeout saturates past ~24.8 days; we only ever schedule to the
      // next midnight, so the cap is never in play. Guard anyway.
      midnightTimer = setTimeout(
        () => {
          setState((prev) => ({ result: resolveDailyQuote() ?? prev.result, pending: false }));
          scheduleNextDay();
        },
        Math.min(msUntilNextDay(), 2_147_483_000),
      );
    };

    scheduleNextDay();

    return () => {
      if (midnightTimer) clearTimeout(midnightTimer);
    };
  }, []);

  return { quote: state.result?.quote ?? null, isLoading: state.pending };
}